import type { HostExecutor } from "./HostExecutor.js";
import type { LatencyBreakdown } from "./types.js";
import { calculateLatencyStats, type LatencyStats } from "./percentiles.js";
import type {
  EvalExpectedToolCall,
  EvalResultInput,
  MCPJamReportingConfig,
} from "./eval-reporting-types.js";
import type {
  EvalTest,
  EvalTestRunOptions,
  EvalRunResult,
  IterationResult,
} from "./EvalTest.js";
import { reportEvalResultsSafely } from "./report-eval-results.js";
import { suiteTestResultsToEvalResultInputs } from "./eval-result-mapping.js";
import { resolveServerReplayConfigs } from "./server-replay-configs.js";
import { buildHostSnapshotMetadata } from "./host-config/internal.js";

/**
 * Configuration for an EvalSuite
 */
export interface EvalSuiteConfig {
  name?: string;
  mcpjam?: MCPJamReportingConfig;
}

/**
 * Result for a single test within the suite
 */
export interface TestResult {
  name: string;
  result: EvalRunResult;
}

/**
 * Result of running an EvalSuite
 */
export interface EvalSuiteResult {
  tests: Map<string, EvalRunResult>;
  aggregate: {
    iterations: number;
    successes: number;
    failures: number;
    accuracy: number;
    tokenUsage: {
      total: number;
      perTest: number[];
    };
    latency: {
      e2e: LatencyStats;
      llm: LatencyStats;
      mcp: LatencyStats;
    };
  };
}

/**
 * EvalSuite - Groups multiple EvalTests and provides aggregate metrics
 *
 * @example
 * ```ts
 * const suite = new EvalSuite({ name: "Math" });
 * suite.add(new EvalTest({
 *   name: "addition",
 *   test: async (executor) => {
 *     const r = await executor.run("Add 2+3");
 *     return r.hasToolCall("add");
 *   },
 * }));
 * suite.add(new EvalTest({
 *   name: "multiply",
 *   test: async (executor) => {
 *     const r = await executor.run("Multiply 4*5");
 *     return r.hasToolCall("multiply");
 *   },
 * }));
 *
 * await suite.run(executor, { iterations: 30 });
 * console.log(suite.accuracy());                 // Aggregate: 0.95
 * console.log(suite.get("addition").accuracy()); // Individual: 0.97
 * ```
 */
export class EvalSuite {
  private name: string;
  private mcpjamConfig?: MCPJamReportingConfig;
  private tests: Map<string, EvalTest> = new Map();
  private lastRunResult: EvalSuiteResult | null = null;

  constructor(config?: EvalSuiteConfig) {
    this.name = config?.name ?? "EvalSuite";
    this.mcpjamConfig = config?.mcpjam;
  }

  /**
   * Add a test to the suite
   */
  add(test: EvalTest): void {
    const name = test.getName();
    if (this.tests.has(name)) {
      throw new Error(`Test with name "${name}" already exists in suite`);
    }
    this.tests.set(name, test);
  }

  /**
   * Get a test by name
   */
  get(name: string): EvalTest | undefined {
    return this.tests.get(name);
  }

  /**
   * Get all tests in the suite
   */
  getAll(): EvalTest[] {
    return Array.from(this.tests.values());
  }

  /**
   * Run all tests in the suite with the given executor and options.
   */
  async run(
    executor: HostExecutor,
    options: EvalTestRunOptions
  ): Promise<EvalSuiteResult> {
    const testResults = new Map<string, EvalRunResult>();
    const suiteReportingConfig = options.mcpjam ?? this.mcpjamConfig;

    // Track total progress across all tests
    const totalIterations = this.tests.size * options.iterations;
    let completedIterations = 0;

    // Run each test sequentially to avoid overwhelming the system
    for (const [name, test] of this.tests) {
      const testOptions: EvalTestRunOptions = {
        ...options,
        mcpjam: suiteReportingConfig
          ? {
              ...suiteReportingConfig,
              enabled: false,
            }
          : undefined,
        __suppressMcpjamAutoSave: true,
        onProgress: options.onProgress
          ? (completed, _total) => {
              // Calculate overall progress
              const overallCompleted = completedIterations + completed;
              options.onProgress!(overallCompleted, totalIterations);
            }
          : undefined,
      };

      const result = await test.run(executor, testOptions);
      testResults.set(name, result);
      completedIterations += options.iterations;
    }

    // Aggregate results
    this.lastRunResult = this.aggregateResults(testResults);
    await this.autoSaveSuiteRunIfConfigured(
      testResults,
      suiteReportingConfig,
      executor
    );
    return this.lastRunResult;
  }

  private async autoSaveSuiteRunIfConfigured(
    testResults: Map<string, EvalRunResult>,
    config: MCPJamReportingConfig | undefined,
    executor: HostExecutor
  ): Promise<void> {
    if (config?.enabled === false) {
      return;
    }
    const apiKey = config?.apiKey ?? process.env.MCPJAM_API_KEY;
    if (!apiKey) {
      return;
    }

    const hostSnapshot = executor.getHostSnapshot?.();
    const hostExtras = hostSnapshot
      ? buildHostSnapshotMetadata(
          hostSnapshot as unknown as Record<string, unknown>,
        )
      : undefined;
    const results = this.buildEvalResultInputs(testResults, config, hostExtras);
    if (results.length === 0) {
      return;
    }

    await reportEvalResultsSafely({
      suiteName: config?.suiteName ?? this.name,
      suiteDescription: config?.suiteDescription,
      serverNames: config?.serverNames,
      serverReplayConfigs: resolveServerReplayConfigs({
        serverReplayConfigs: config?.serverReplayConfigs,
        serverNames: config?.serverNames,
        agent: executor,
      }),
      notes: config?.notes,
      passCriteria: config?.passCriteria,
      externalRunId: config?.externalRunId,
      framework: config?.framework,
      ci: config?.ci,
      apiKey,
      baseUrl: config?.baseUrl,
      strict: config?.strict,
      results,
    });
  }

  private buildEvalResultInputs(
    testResults: Map<string, EvalRunResult>,
    reporting?: MCPJamReportingConfig,
    hostExtras?: Record<string, string | number | boolean>,
  ): EvalResultInput[] {
    const expectedToolCallsByTest: Record<string, EvalExpectedToolCall[]> = {};
    for (const [name, test] of this.tests) {
      const expected = test.getConfig().expectedToolCalls;
      if (expected) {
        expectedToolCallsByTest[name] = expected;
      }
    }
    return suiteTestResultsToEvalResultInputs(
      testResults,
      Object.keys(expectedToolCallsByTest).length > 0
        ? expectedToolCallsByTest
        : undefined,
      reporting?.failOnToolError,
      hostExtras,
    );
  }

  private aggregateResults(
    testResults: Map<string, EvalRunResult>
  ): EvalSuiteResult {
    const results = Array.from(testResults.values());

    // Aggregate iterations
    const allIterations: IterationResult[] = results.flatMap(
      (r) => r.iterationDetails
    );
    const totalIterations = allIterations.length;
    const totalSuccesses = allIterations.filter((r) => r.passed).length;
    const totalFailures = totalIterations - totalSuccesses;

    // Aggregate latencies
    const allLatencies: LatencyBreakdown[] = results.flatMap(
      (r) => r.latency.perIteration
    );

    const defaultStats: LatencyStats = {
      min: 0,
      max: 0,
      mean: 0,
      p50: 0,
      p95: 0,
      count: 0,
    };

    const e2eValues = allLatencies.map((l) => l.e2eMs);
    const llmValues = allLatencies.map((l) => l.llmMs);
    const mcpValues = allLatencies.map((l) => l.mcpMs);

    // Token usage
    const totalTokens = results.reduce((sum, r) => sum + r.tokenUsage.total, 0);
    const perTestTokens = results.map((r) => r.tokenUsage.total);

    return {
      tests: testResults,
      aggregate: {
        iterations: totalIterations,
        successes: totalSuccesses,
        failures: totalFailures,
        accuracy: totalIterations > 0 ? totalSuccesses / totalIterations : 0,
        tokenUsage: {
          total: totalTokens,
          perTest: perTestTokens,
        },
        latency: {
          e2e:
            e2eValues.length > 0
              ? calculateLatencyStats(e2eValues)
              : defaultStats,
          llm:
            llmValues.length > 0
              ? calculateLatencyStats(llmValues)
              : defaultStats,
          mcp:
            mcpValues.length > 0
              ? calculateLatencyStats(mcpValues)
              : defaultStats,
        },
      },
    };
  }

  /**
   * Get the aggregate accuracy across all tests
   */
  accuracy(): number {
    if (!this.lastRunResult) {
      throw new Error("No run results available. Call run() first.");
    }
    return this.lastRunResult.aggregate.accuracy;
  }

  /**
   * Get the aggregate recall (same as accuracy in basic context)
   */
  recall(): number {
    if (!this.lastRunResult) {
      throw new Error("No run results available. Call run() first.");
    }
    return this.accuracy();
  }

  /**
   * Get the aggregate precision (same as accuracy in basic context)
   */
  precision(): number {
    if (!this.lastRunResult) {
      throw new Error("No run results available. Call run() first.");
    }
    return this.accuracy();
  }

  /**
   * Get the aggregate true positive rate (same as recall)
   */
  truePositiveRate(): number {
    if (!this.lastRunResult) {
      throw new Error("No run results available. Call run() first.");
    }
    return this.recall();
  }

  /**
   * Get the aggregate false positive rate
   */
  falsePositiveRate(): number {
    if (!this.lastRunResult) {
      throw new Error("No run results available. Call run() first.");
    }
    const { failures, iterations } = this.lastRunResult.aggregate;
    return iterations > 0 ? failures / iterations : 0;
  }

  /**
   * Get the average token use per iteration across all tests
   */
  averageTokenUse(): number {
    if (!this.lastRunResult) {
      throw new Error("No run results available. Call run() first.");
    }
    const { total } = this.lastRunResult.aggregate.tokenUsage;
    const { iterations } = this.lastRunResult.aggregate;
    return iterations > 0 ? total / iterations : 0;
  }

  /**
   * Get the full suite results
   */
  getResults(): EvalSuiteResult | null {
    return this.lastRunResult;
  }

  /**
   * Get the name of the suite
   */
  getName(): string {
    return this.name;
  }

  /**
   * Get the number of tests in the suite
   */
  size(): number {
    return this.tests.size;
  }
}
